/**
 * #926: the one order of a connector's versions, for every reader (the editor's Connector version
 * dropdown, `GET /connectors` and `/connectors/:name`, and so MCP `get_connector`). The default
 * ("Latest") comes first, then the rest newest to oldest by version number, never by text: v1.10.0
 * is newer than v1.9.0. The stored order (`connector_versions` has no order of its own) is never
 * relied on. A tag that is not a version number (`skydata-dev`) sorts after every one that is,
 * by text, so the order is the same on every call.
 */

interface OrderableVersion {
  tag: string;
  isDefault: boolean;
}

/** `v1.6.0`, `1.5.4`, `1.3`: the numbers, or null for a tag that is not a version number. */
function versionNumbers(tag: string): number[] | null {
  const match = /^v?(\d+(?:\.\d+)*)(?:[-+].*)?$/i.exec(tag.trim());
  return match ? match[1]!.split(".").map(Number) : null;
}

/** Newest first: a negative answer puts `a` before `b`. */
export function compareVersionTagsNewestFirst(a: string, b: string): number {
  const left = versionNumbers(a);
  const right = versionNumbers(b);
  if (left && !right) return -1;
  if (!left && right) return 1;
  if (left && right) {
    const length = Math.max(left.length, right.length);
    for (let index = 0; index < length; index += 1) {
      const difference = (right[index] ?? 0) - (left[index] ?? 0);
      if (difference !== 0) return difference;
    }
  }
  return a < b ? -1 : a > b ? 1 : 0;
}

/** A new array: the default first, then newest to oldest. */
export function orderConnectorVersions<T extends OrderableVersion>(versions: readonly T[]): T[] {
  return [...versions].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
    return compareVersionTagsNewestFirst(a.tag, b.tag);
  });
}
